import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef } from 'react'
import { makeStyles } from '@material-ui/styles'
import { useTranslation } from 'react-i18next'
import moment from 'moment'
import { Element } from 'react-scroll'
import ScrollUp from 'components/common/icons/timelineV2/mini-timeline/ScrollUp'
import ScrollDown from 'components/common/icons/timelineV2/mini-timeline/ScrollDown'
import useInViewport from 'hooks/useInViewport'
import Tooltip from 'components/common/Tooltip'
import { trackEvent } from 'utils/analyticsUtils'
import MiniTimelineButton from '../MiniTimelineButton'
import MiniTimelineThemeSelector from '../Themes/MiniTimelineThemeSelector'
import TimelineStack from './TimelineStack'
import buildTimeBasedLayout, { MONTH_HEIGHT, MONTH_LABEL_HEIGHT } from './timeBasedLayout'

const useStyles = makeStyles(theme => ({
  miniTimelineRoot: {
    position: 'relative',
    height: '100%',
    minWidth: 65
  },
  timelineContainer: {
    height: '100%',
    overflowY: 'scroll',
    padding: '8px 10px',
    '&::-webkit-scrollbar': {
      display: 'none'
    }
  },
  months: {
    position: 'relative'
  },
  verticalAxis: {
    // Inside the month list rather than the root, so it spans the whole scrollable column. Anchored
    // to the root it is a fixed backdrop one scroll-button short, leaving the foot of the column
    // without an axis at every scroll position.
    position: 'absolute',
    top: 0,
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '1px',
    background: '#CED9E7'
  },
  month: {
    position: 'relative',
    height: MONTH_HEIGHT
  },
  unavailableNotice: {
    position: 'relative',
    zIndex: 2,
    margin: '0 auto 4px',
    width: 'fit-content',
    padding: '2px 6px',
    borderRadius: 2,
    background: 'var(--bg-color-timelineV2)',
    color: 'var(--text-color-miniTimeline-date)',
    fontFamily: 'Dazzed',
    fontSize: 9,
    lineHeight: '12px',
    textAlign: 'center',
    cursor: 'default'
  },
  monthLabel: {
    position: 'absolute',
    // Above every marker: the band leaves about two pixels of clearance at a month boundary, so the
    // label staying legible should not depend on that arithmetic holding.
    zIndex: 1000,
    top: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 46,
    height: MONTH_LABEL_HEIGHT,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    // The chip masks the axis line running behind the label.
    background: 'var(--bg-color-timelineV2)',
    fontWeight: '400!important',
    fontSize: 10,
    lineHeight: '10px',
    color: 'var(--text-color-miniTimeline-date)',
    fontFamily: 'Dazzed',
    userSelect: 'none'
  }
}))

/** Leaves the scan being scrolled to clear of the top edge rather than flush against it. */
const SCROLL_TO_ITEM_MARGIN = 150

const TIMELINE_CONTAINER_ID = 'time-based-mini-timeline-container'
const BOTTOM_SCROLL_ID = 'time-based-mini-timeline-bottom'
const TOP_SCROLL_ID = 'time-based-mini-timeline-top'

/**
 * The time-based mini timeline.
 *
 * The axis reads forwards: treatment starts at the top and the foot of it is now. Every month is
 * the same height whether it holds ten scans or none, and each scan sits at its own date inside its
 * month - so a gap in treatment is visible as a gap, which the scan-based timeline cannot show. Scans that fall too close together are pushed just far enough apart to stay
 * separately visible, and scans that were due and never arrived are drawn from the patient's
 * cycles.
 */
const TimeBasedMiniTimeline = ({
  timelineItems = [],
  missedScansUnavailable = false,
  selectedTimelineItemId,
  setSelectedTimelineItem,
  selectedTheme,
  onSelectTheme,
  themeOptions = [],
  analyticsPayload = {},
  className = ''
}) => {
  const classes = useStyles()
  const { t } = useTranslation()

  const timelineTopRef = useRef()
  const timelineBottomRef = useRef()
  const { isInViewport: isScrolledToTop } = useInViewport(timelineTopRef, { threshold: 0.1 })
  const { isInViewport: isScrolledToBottom } = useInViewport(timelineBottomRef, { threshold: 0.1 })

  const months = useMemo(() => buildTimeBasedLayout({ items: timelineItems }), [timelineItems])

  // The column is scrolled directly rather than through react-scroll, whose scrollTo moves nothing
  // here - the scan-based timeline's buttons are dead for the same reason. Animated scrolling is
  // what fails: a smooth scrollTo on this container is a no-op too, while an instant one works, so
  // the column jumps rather than travels.
  const scrollTo = useCallback(top => {
    const container = document.getElementById(TIMELINE_CONTAINER_ID)
    if (container) {
      container.scrollTop = Math.max(top, 0)
    }
  }, [])

  const scrollToElement = useCallback(
    elementId => {
      const container = document.getElementById(TIMELINE_CONTAINER_ID)
      const target = document.getElementById(elementId)
      if (!container || !target) {
        return
      }

      const offset = target.getBoundingClientRect().top - container.getBoundingClientRect().top + container.scrollTop
      scrollTo(offset - SCROLL_TO_ITEM_MARGIN)
    },
    [scrollTo]
  )

  const handleScrollToTop = useCallback(() => {
    trackEvent('Mini timeline - scroll to top clicked', analyticsPayload)
    scrollTo(0)
  }, [scrollTo, analyticsPayload])

  const handleScrollToBottom = useCallback(() => {
    trackEvent('Mini timeline - scroll to bottom clicked', analyticsPayload)
    const container = document.getElementById(TIMELINE_CONTAINER_ID)
    scrollTo(container ? container.scrollHeight : 0)
  }, [scrollTo, analyticsPayload])

  const handleSelectTimelineItem = useCallback(
    ({ timelineItem, section }) => {
      setSelectedTimelineItem({
        timelineItemId: timelineItem.id,
        section,
        analyticsPayload: {
          ...analyticsPayload,
          source: 'Mini Timeline',
          layout: 'time-based',
          timelineItemType: timelineItem.type,
          scanNumber: timelineItem.payload.scanNumber
        }
      })
    },
    [setSelectedTimelineItem, analyticsPayload]
  )

  const hasJumpedToNow = useRef(false)
  const scrolledToItemId = useRef(null)

  useLayoutEffect(() => {
    // Before paint, and once: the axis ends at now, and that is where the doctor should find it
    // rather than watch it travel there. Animating it loses anyway - the layout changes again as
    // the patient's cycles and treatment arrive, which restarts the animation from the top.
    const container = document.getElementById(TIMELINE_CONTAINER_ID)
    if (hasJumpedToNow.current || !months.length || !container) {
      return
    }

    hasJumpedToNow.current = true
    container.scrollTop = container.scrollHeight
  }, [months])

  useEffect(() => {
    // A scan picked anywhere else - the feed, a deep link - brings the axis to it. It waits for the
    // scan itself, not merely for months to exist: scans and the missed ones derived from cycles
    // arrive separately, so the axis can be drawn while the one being looked for is still missing,
    // and scrollToElement has nothing to report back. Recorded once it has run, so a later
    // re-layout does not drag the doctor back to it.
    const isDrawn = timelineItems.some(item => item.id === selectedTimelineItemId)
    if (!selectedTimelineItemId || !isDrawn || scrolledToItemId.current === selectedTimelineItemId) {
      return
    }

    scrolledToItemId.current = selectedTimelineItemId
    scrollToElement(selectedTimelineItemId)
  }, [selectedTimelineItemId, timelineItems, scrollToElement])

  return (
    <div className={[classes.miniTimelineRoot, className].join(' ')}>
      {themeOptions?.length > 1 && (
        <MiniTimelineThemeSelector
          selectedTheme={selectedTheme}
          onSelectTheme={onSelectTheme}
          themeOptions={themeOptions}
          analyticsPayload={analyticsPayload}
        />
      )}
      <MiniTimelineButton
        icon={<ScrollUp />}
        tooltip={t('pages.patients.selectedPatient.timelineV2.miniTimeline.buttons.scrollUpTooltip')}
        tooltipPlacement="top"
        onClick={handleScrollToTop}
        disabled={isScrolledToTop}
      />
      <Element className={classes.timelineContainer} id={TIMELINE_CONTAINER_ID}>
        <div ref={timelineTopRef}>
          <Element id={TOP_SCROLL_ID} />
        </div>
        {missedScansUnavailable && (
          <Tooltip
            placement="right"
            value={t('pages.patients.selectedPatient.timelineV2.miniTimeline.missedScansUnavailableTooltip')}
          >
            <div className={classes.unavailableNotice}>
              {t('pages.patients.selectedPatient.timelineV2.miniTimeline.missedScansUnavailable')}
            </div>
          </Tooltip>
        )}
        <div className={classes.months}>
          <div className={classes.verticalAxis}></div>
          {months.map(({ monthDate, stacks }) => (
            <div className={classes.month} key={monthDate}>
              <div className={classes.monthLabel}>{moment(monthDate).format("MMM 'YY").toUpperCase()}</div>
              {stacks.map((stack, stackIndex) => (
                <TimelineStack
                  key={stack.items[0].id}
                  stack={stack}
                  depth={stackIndex}
                  selectedTimelineItemId={selectedTimelineItemId}
                  onSelect={handleSelectTimelineItem}
                  analyticsPayload={analyticsPayload}
                />
              ))}
            </div>
          ))}
        </div>
        <div ref={timelineBottomRef}>
          <Element id={BOTTOM_SCROLL_ID} />
        </div>
      </Element>
      <MiniTimelineButton
        icon={<ScrollDown />}
        tooltip={t('pages.patients.selectedPatient.timelineV2.miniTimeline.buttons.scrollDownTooltip')}
        tooltipPlacement="top"
        onClick={handleScrollToBottom}
        disabled={isScrolledToBottom}
      />
    </div>
  )
}

export default React.memo(TimeBasedMiniTimeline)
